import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, ArrowUpRight, CheckCircle2, XCircle, Users, Handshake, AlertTriangle, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { formatDate } from "@/lib/format";
import TonalBadge from "@/components/TonalBadge";

function AddOfferDialog({ open, onClose, assetId, kind }) {
  const qc = useQueryClient();
  const isClients = kind === "clients";
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState([]);
  const [confirmStale, setConfirmStale] = useState(false);

  const { data: candidates = [] } = useQuery({
    queryKey: [isClients ? "offer-leads" : "offer-brokers"],
    queryFn: async () => (await api.get(isClients ? "/leads" : "/brokers", { params: { limit: 300 } })).data,
    enabled: open,
  });

  const idKey = "id";
  const nameKey = isClients ? "lead_name" : "broker_name";
  const codeKey = isClients ? "lead_code" : "broker_code";

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = Array.isArray(candidates) ? candidates : (candidates.items || []);
    if (!q) return list.slice(0, 100);
    return list.filter((c) => `${c[nameKey] || ""} ${c[codeKey] || ""}`.toLowerCase().includes(q)).slice(0, 100);
  }, [candidates, query, nameKey, codeKey]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const add = useMutation({
    mutationFn: async () => api.post(`/offers/assets/${assetId}/${kind}`, {
      [isClients ? "lead_ids" : "broker_ids"]: selected,
      confirm_stale: confirmStale,
    }),
    onSuccess: (res) => {
      const { created, reoffered, in_pipeline, blocked } = res.data;
      if (created.length) toast.success(`Logged ${created.length} offer(s)`);
      if (reoffered.length) toast.info(`Re-offered to ${reoffered.length}`);
      if (in_pipeline.length) toast.info(`${in_pipeline.length} already in active pipeline`);
      if (blocked.length) toast.warning(`${blocked.length} blocked: ${blocked.map((b) => b.reason).join("; ")}`);
      qc.invalidateQueries({ queryKey: ["offered", kind, assetId] });
      onClose();
    },
    onError: (e) => {
      const detail = e?.response?.data?.detail || "Failed";
      if (e?.response?.status === 409 && detail.toLowerCase().includes("confirm to offer")) {
        setConfirmStale(true);
        toast.warning(detail);
      } else {
        toast.error(detail);
      }
    },
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid={`add-offer-dialog-${kind}`}>
        <DialogHeader><DialogTitle>Offer Asset to {isClients ? "Clients (Leads)" : "Brokers"}</DialogTitle></DialogHeader>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Search ${isClients ? "leads" : "brokers"}…`} className="pl-8" data-testid="add-offer-search" />
        </div>
        <div className="mt-2 max-h-72 overflow-y-auto border border-slate-200 rounded divide-y divide-slate-100">
          {filtered.length === 0 ? (
            <div className="p-4 text-sm text-slate-500">No matches.</div>
          ) : filtered.map((c) => (
            <label key={c[idKey]} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer" data-testid={`add-offer-option-${c[idKey]}`}>
              <input type="checkbox" checked={selected.includes(c[idKey])} onChange={() => toggle(c[idKey])} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">{c[nameKey]}</div>
                <div className="text-[11px] text-slate-500 font-mono-num">{c[codeKey]}</div>
              </div>
            </label>
          ))}
        </div>
        {confirmStale && (
          <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="offer-stale-warning">
            <AlertTriangle className="h-4 w-4" /> Asset is not Active — logging the offer anyway on submit.
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => add.mutate()} disabled={selected.length === 0 || add.isPending} className="bg-slate-900 hover:bg-slate-800" data-testid="add-offer-submit">
            {confirmStale ? "Offer anyway" : `Add (${selected.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OfferPanel({ assetId, kind }) {
  const qc = useQueryClient();
  const isClients = kind === "clients";
  const [showAdd, setShowAdd] = useState(false);

  const { data: entries = [] } = useQuery({
    queryKey: ["offered", kind, assetId],
    queryFn: async () => (await api.get(`/offers/assets/${assetId}/${kind}`)).data,
  });

  const promote = useMutation({
    mutationFn: async (offerId) => api.post(`/offers/${kind}/${offerId}/promote`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["offered", kind, assetId] });
      qc.invalidateQueries({ queryKey: ["asset-matches", assetId] });
      qc.invalidateQueries({ queryKey: ["asset-shares", assetId] });
      toast.success(isClients ? "Promoted to active match" : "Promoted to active share");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const decline = useMutation({
    mutationFn: async (offerId) => api.post(`/offers/${kind}/${offerId}/decline`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["offered", kind, assetId] });
      toast.success("Marked Declined");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const removeOffer = useMutation({
    mutationFn: async (offerId) => api.delete(`/offers/clients/${offerId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["offered", kind, assetId] });
      toast.success("Removed");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const Icon = isClients ? Users : Handshake;

  return (
    <div className="bg-white border border-slate-200 rounded-md p-5" data-testid={`offered-panel-${kind}`}>
      <div className="flex items-center justify-between">
        <div className="label-eyebrow flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" /> {isClients ? "Clients" : "Brokers"} ({entries.length})</div>
        <Button size="sm" variant="outline" onClick={() => setShowAdd(true)} data-testid={`offer-add-${kind}`}><Plus className="h-3.5 w-3.5 mr-1.5" />Add</Button>
      </div>

      {entries.length === 0 ? (
        <div className="text-sm text-slate-500 mt-3">Not offered to any {isClients ? "client" : "broker"} yet.</div>
      ) : (
        <table className="data-table w-full mt-3">
          <thead><tr>
            <th className="label-eyebrow !text-slate-600">{isClients ? "Lead" : "Broker"}</th>
            <th className="label-eyebrow !text-slate-600">Date / Stage</th>
            <th className="label-eyebrow !text-slate-600">Status</th>
            <th className="label-eyebrow !text-slate-600 text-right">Action</th>
          </tr></thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.offer_id || e.match_id || e.share_id} className="border-t border-slate-100" data-testid={`offer-row-${e.offer_id || e.match_id || e.share_id}`}>
                <td className="text-sm font-medium text-slate-900">
                  {e.name}
                  <div className="text-[10px] text-slate-400 font-mono-num">{e.lead_code || e.broker_code}</div>
                </td>
                <td className="text-sm text-slate-600">
                  {e.entry_type === "pipeline"
                    ? <TonalBadge listType={isClients ? "match_status" : "broker_share_status"} label={e.stage_label} />
                    : <span className="font-mono-num">{formatDate(e.offered_date)}{e.last_reoffered_date ? ` · re ${formatDate(e.last_reoffered_date)}` : ""}</span>}
                </td>
                <td>
                  {e.entry_type === "pipeline"
                    ? <span className="text-[11px] text-slate-500">In pipeline</span>
                    : <TonalBadge listType="offer_status" label={e.offer_status_label} />}
                </td>
                <td className="text-right">
                  {e.entry_type === "pipeline" ? (
                    <Link to={isClients ? "/kanban" : `/brokers/${e.broker_id}`} className="text-xs font-semibold text-slate-700 hover:underline inline-flex items-center gap-1" data-testid={`offer-open-${e.match_id || e.share_id}`}>
                      Open <ArrowUpRight className="h-3 w-3" />
                    </Link>
                  ) : (
                    <div className="flex flex-wrap gap-1.5 justify-end">
                      {e.offer_status_label === "Offered" && (
                        <>
                          <Button size="sm" variant="outline" title="Promote to active pipeline" className="h-7 w-7 p-0 bg-emerald-50 border-emerald-200 hover:bg-emerald-100" disabled={promote.isPending} onClick={() => promote.mutate(e.offer_id)} data-testid={`offer-promote-${e.offer_id}`}>
                            <CheckCircle2 className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="outline" title="Mark declined" className="h-7 w-7 p-0 border-red-200 text-red-700 hover:bg-red-50" disabled={decline.isPending} onClick={() => decline.mutate(e.offer_id)} data-testid={`offer-decline-${e.offer_id}`}>
                            <XCircle className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                      {isClients ? (
                        <Button size="sm" variant="ghost" title="Remove offer" className="h-7 w-7 p-0 text-red-700 hover:bg-red-50" disabled={removeOffer.isPending} onClick={() => { if (window.confirm("Remove this client offer? This deletes the offer record (the Lead's Assets Offered list updates too).")) removeOffer.mutate(e.offer_id); }} data-testid={`offer-remove-${e.offer_id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      ) : (
                        e.offer_status_label !== "Offered" && <span className="text-[11px] text-slate-400">—</span>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <AddOfferDialog open={showAdd} onClose={() => setShowAdd(false)} assetId={assetId} kind={kind} />
    </div>
  );
}

export default function OfferedToSection({ assetId, showClients = true }) {
  return (
    <div className={`grid grid-cols-1 ${showClients ? "lg:grid-cols-2" : ""} gap-5`} data-testid="offered-to-section">
      {showClients && <OfferPanel assetId={assetId} kind="clients" />}
      <OfferPanel assetId={assetId} kind="brokers" />
    </div>
  );
}
